import { enumLabel } from '../../utils/enumLabels';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Checkbox, Button, Card, Row, Col, Modal, message, Divider } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CNPValidator } from '../../utils/cnpValidator';
import { patientApi, CreatePatientDto } from '../../api/patientApi';
import { useAuthStore } from '../../store/authStore';
import { getProgramOptionsForRole, getRoleProgramType } from '../../utils/flowAccess';

interface PatientFormData {
  cnp: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  county?: string;
  programType: 'DERMATO' | 'HEPATO' | 'BOTH';
  personalDataConsent: boolean;
  medicalDataConsent: boolean;
}

const PatientNewPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const roleProgramType = getRoleProgramType(roleName);
  const programOptions = getProgramOptionsForRole(roleName);
  const defaultProgramType = roleProgramType || 'DERMATO';
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PatientFormData | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [sex, setSex] = useState<'M' | 'F' | null>(null);

  const handleCNPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cnp = e.target.value.replace(/\s/g, '');

    if (cnp.length === 13) {
      const result = CNPValidator.validate(cnp);

      if (result.valid && result.dateOfBirth && result.sex) {
        setDateOfBirth(result.dateOfBirth);
        setSex(result.sex);
        message.success('CNP valid. Date extrase automat.');
      } else {
        setDateOfBirth(null);
        setSex(null);
        form.setFields([
          {
            name: 'cnp',
            errors: [result.error || 'CNP invalid'],
          },
        ]);
      }
    } else {
      setDateOfBirth(null);
      setSex(null);
    }
  };

  const handlePreview = (values: PatientFormData) => {
    setPreviewData(values);
  };

  const handleSubmit = async () => {
    if (!previewData || !dateOfBirth || !sex) return;

    setLoading(true);
    try {
      const createData: CreatePatientDto = {
        cnp: previewData.cnp,
        firstName: previewData.firstName,
        lastName: previewData.lastName,
        phone: previewData.phone,
        email: previewData.email,
        address: previewData.address,
        city: previewData.city,
        county: previewData.county,
        programType: roleProgramType || previewData.programType,
        personalDataConsent: previewData.personalDataConsent,
        medicalDataConsent: previewData.medicalDataConsent,
        consentUserAgent: navigator.userAgent,
      };

      const patient = await patientApi.create(createData);

      message.success('Pacient inrolat cu succes!');
      setPreviewData(null);
      if (patient.programType === 'HEPATO') {
        navigate(`/patients/${patient.id}/hepato-wizard`);
      } else {
        navigate(`/patients/${patient.id}/dermato-wizard`);
      }
    } catch (error: any) {
      if (error.response?.status === 409 && previewData.cnp) {
        const existingPatient = await patientApi.findByCnp(previewData.cnp);

        if (existingPatient) {
          message.info('Pacientul există deja. Continuăm cu fișa existentă.');
          setPreviewData(null);
          if (existingPatient.programType === 'HEPATO') {
            navigate(`/patients/${existingPatient.id}/hepato-wizard`);
          } else {
            navigate(`/patients/${existingPatient.id}/dermato-wizard`);
          }
          return;
        }
      }

      message.error(error.response?.data?.message || 'Eroare la inrolare pacient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Inrolare Pacient Nou</h1>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePreview}
          initialValues={{ programType: defaultProgramType }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="CNP"
                name="cnp"
                rules={[
                  { required: true, message: 'CNP este obligatoriu' },
                  { len: 13, message: 'CNP trebuie sa aiba 13 cifre' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const result = CNPValidator.validate(value);
                      return result.valid ? Promise.resolve() : Promise.reject(result.error);
                    },
                  },
                ]}
              >
                <Input
                  placeholder="1234567890123"
                  maxLength={13}
                  onChange={handleCNPChange}
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Tip Program"
                name="programType"
                rules={[{ required: true, message: 'Selectati tipul programului' }]}
              >
                <Select
                  disabled={Boolean(roleProgramType)}
                  options={programOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          {dateOfBirth && sex && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <strong>Data nasterii:</strong> {dayjs(dateOfBirth).format('DD.MM.YYYY')}
                </Col>
                <Col span={12}>
                  <strong>Sex:</strong> {sex === 'M' ? 'Masculin' : 'Feminin'}
                </Col>
              </Row>
            </div>
          )}

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Prenume"
                name="firstName"
                rules={[
                  { required: true, message: 'Prenumele este obligatoriu' },
                  { min: 2, message: 'Prenume prea scurt' },
                ]}
              >
                <Input placeholder="Ion" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Nume"
                name="lastName"
                rules={[
                  { required: true, message: 'Numele este obligatoriu' },
                  { min: 2, message: 'Nume prea scurt' },
                ]}
              >
                <Input placeholder="Popescu" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Telefon"
                name="phone"
                rules={[
                  {
                    pattern: /^(\+4|0)[0-9]{9}$/,
                    message: 'Format: +40712345678 sau 0712345678',
                  },
                ]}
              >
                <Input placeholder="+40712345678" prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: 'email', message: 'Email invalid' }]}
              >
                <Input placeholder="ion.popescu@example.com" prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Adresa" name="address">
            <Input.TextArea placeholder="Str. Exemplu, Nr. 1" rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Oras" name="city">
                <Input placeholder="Bucuresti" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Judet" name="county">
                <Input placeholder="Bucuresti" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Consimtamant GDPR</Divider>

          <Form.Item
            name="personalDataConsent"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject('Consimtamantul este obligatoriu'),
              },
            ]}
          >
            <Checkbox name="informedConsentGiven">
              Sunt de acord cu prelucrarea datelor cu caracter personal conform Politicii GDPR
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="medicalDataConsent"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject('Consimtamantul este obligatoriu'),
              },
            ]}
          >
            <Checkbox name="dataProcessingConsentGiven">
              Sunt de acord cu prelucrarea datelor medicale conform Regulamentului Medical
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" disabled={!dateOfBirth || !sex}>
              Previzualizare si Salvare
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate('/patients')}>
              Anuleaza
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        title="Confirmare Date Pacient"
        open={!!previewData}
        onOk={handleSubmit}
        onCancel={() => setPreviewData(null)}
        okText="Confirma si Inroleaza"
        cancelText="Inapoi"
        confirmLoading={loading}
        width={600}
      >
        {previewData && dateOfBirth && sex && (
          <div>
            <h3>Verificati datele inainte de inrolare:</h3>
            <Divider />
            <Row gutter={[16, 8]}>
              <Col span={8}><strong>CNP:</strong></Col>
              <Col span={16}>{previewData.cnp}</Col>

              <Col span={8}><strong>Nume complet:</strong></Col>
              <Col span={16}>{previewData.firstName} {previewData.lastName}</Col>

              <Col span={8}><strong>Data nasterii:</strong></Col>
              <Col span={16}>{dayjs(dateOfBirth).format('DD.MM.YYYY')}</Col>

              <Col span={8}><strong>Sex:</strong></Col>
              <Col span={16}>{sex === 'M' ? 'Masculin' : 'Feminin'}</Col>

              <Col span={8}><strong>Tip program:</strong></Col>
              <Col span={16}>{enumLabel(previewData.programType)}</Col>

              {previewData.phone && (
                <>
                  <Col span={8}><strong>Telefon:</strong></Col>
                  <Col span={16}>{previewData.phone}</Col>
                </>
              )}

              {previewData.email && (
                <>
                  <Col span={8}><strong>Email:</strong></Col>
                  <Col span={16}>{previewData.email}</Col>
                </>
              )}

              {previewData.address && (
                <>
                  <Col span={8}><strong>Adresa:</strong></Col>
                  <Col span={16}>{previewData.address}</Col>
                </>
              )}

              {previewData.city && (
                <>
                  <Col span={8}><strong>Oras:</strong></Col>
                  <Col span={16}>{previewData.city}</Col>
                </>
              )}

              {previewData.county && (
                <>
                  <Col span={8}><strong>Judet:</strong></Col>
                  <Col span={16}>{previewData.county}</Col>
                </>
              )}
            </Row>
            <Divider />
            <p style={{ color: '#237804' }}>
              Consimtamant prelucrare date personale<br />
              Consimtamant prelucrare date medicale
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientNewPage;
